import { React, useState, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "../../Utils/axiosConfig";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";

import { TogglePasswordSignup } from "./TogglePassword";
import { navigateToOtp } from "../../Utils/navigateToOtp";
import { RecoveryContext } from "../../App";
import { LoadingPage } from "../LoadingPage";
import "./Signup.css";

import logo from "../Assets/Logo.svg";
import google from "../Assets/Google.svg";
import gamereview from "../Assets/Game-review.svg";
import ai from "../Assets/AI.svg";
import puzzles from "../Assets/Puzzles.svg";
import book from "../Assets/Book.svg";
import visualisation from "../Assets/Visualisation.svg";

export function Signup() {
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const {
    email,
    setEmail,
    fromSignup,
    setFromSignup,
    fname,
    setFname,
    lname,
    setLname,
    username,
    setUsername,
    password,
    setPassword,
  } = useContext(RecoveryContext);
  const login = useGoogleLogin({
    onSuccess: (tokenResponse) => console.log(tokenResponse),
  });

  const navigate = useNavigate();

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePassword = (password) => {
    const re = /^(?=.*[A-Z]).{8,22}$/;
    return re.test(password);
  };

  const validateUsername = (username) => {
    const re = /^[a-zA-Z][a-zA-Z0-9_-]{0,16}$/;
    return re.test(username);
  };

  const validateName = (name) => {
    const re = /^[A-Za-z]+$/;
    return re.test(name) && name.length <= 17;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let error = "";

    if (!fname) {
      error = "First name cannot be empty.";
    } else if (!validateName(fname)) {
      error = "Invalid first name format or exceeded max length.";
    } else if (!lname) {
      error = "Last name cannot be empty.";
    } else if (!validateName(lname)) {
      error = "Invalid last name format or exceeded max length.";
    } else if (!username) {
      error = "Username cannot be empty.";
    } else if (!validateUsername(username)) {
      error = "Invalid username format.";
    } else if (!validateEmail(email)) {
      error = "Invalid email format.";
    } else if (!validatePassword(password)) {
      error =
        "Password must be 8-22 characters with at least one uppercase letter.";
    }

    if (error) {
      setErrorMessage(error);
      return;
    }

    try {
      const usernameCheckResponse = await axios.post(
        "/check-username",
        { username }
      );

      const emailCheckResponse = await axios.post(
        "/check-email",
        { email }
      );

      if (
        usernameCheckResponse.data.message === "Username is available." &&
        emailCheckResponse.data.message === "Email is available."
      ) {
        sessionStorage.setItem("username", username);
        setFromSignup(true);
        setLoading(true);
      }
    } catch (err) {
      if (err.response && err.response.status === 400) {
        if (err.response.data.message === "Username is already taken.") {
          setErrorMessage("Username already exists");
        } else if (err.response.data.message === "Email already exists.") {
          setErrorMessage("Email already exists.");
        }
      } else {
        console.error(err);
        setErrorMessage("An error occurred during signup.");
      }
    }
  };

  useEffect(() => {
    setEmail("");
    setFname("");
    setLname("");
    setUsername("");
    setPassword("");
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (loading) {
      (async () => {
        try {
          await navigateToOtp(email, navigate, setErrorMessage, fromSignup);
        } catch (err) {
          console.error(err);
          setErrorMessage("An error occurred while sending OTP.");
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [loading, fromSignup, email, navigate, setErrorMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);
  if (loading) {
    return <LoadingPage />;
  }
  return (
    <div className="container login-mobile-layout signup-mobile-layout">
      <header className="header">
        <img className="logo-image" src={logo} alt="logo" />
        <div className="logo-text">
          <span className="C-logo">C</span>
          <span className="hezzle-logo">
            he<span className="Z-1">Z</span>
            <span className="Z-2">Z</span>le
          </span>
        </div>
        <Link to="/login" className="login-button">
          Log in
        </Link>
      </header>
      <div className="gradient-line"></div>

      <div className="left-half">
        <img className="left-image" src={logo} alt="logo" />
        <div className="features">
          <ul>
            <li>
              <img src={gamereview} className="gamereview-svg" alt="" />
              Enhance your game analysis!
            </li>
            <br />
            <li>
              <img src={ai} className="ai-svg" alt="" />
              Get AI-assisted move breakdown.
            </li>
            <br />
            <li>
              <img src={puzzles} className="puzzles-svg" alt="" />
              Exclusive puzzles from your games!
            </li>
            <br />
            <li>
              <img src={book} className="book-svg" alt="" />
              Master your openings and middle game.
            </li>
            <br />
            <li>
              <img src={visualisation} className="visualisation-svg" alt="" />
              Level up your visualization skills!
            </li>
            <br />
          </ul>
        </div>
      </div>

      <div className="signup-form">
        <div className="line"></div>
        <h2 className="welcome">Welcome to Chezzle!</h2>
        <form className="form" onSubmit={handleSubmit}>
          <div className="name">
            <input
              type="text"
              placeholder="First name"
              className="first-name"
              onChange={(e) => setFname(e.target.value)}
            />
            <input
              type="text"
              placeholder="Last name"
              className="last-name"
              onChange={(e) => setLname(e.target.value)}
            />
          </div>
          <input
            type="text"
            placeholder="Username"
            className="username"
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
          />
          <input
            type="email"
            placeholder="Email"
            className="email"
            onChange={(e) => setEmail(e.target.value)}
          />
          <div className="password-wrapper">
            <input
              type={visiblePassword ? "text" : "password"}
              placeholder="Password"
              className="password-signup"
              onChange={(e) => setPassword(e.target.value)}
            />
            <TogglePasswordSignup onToggle={setVisiblePassword} />
          </div>
          <button type="submit" className="create-account-button">
            Create account
          </button>
        </form>
        <div className="or-and-line-signup">
          <div className="orline1"></div>
          <br />
          <div className="or">or</div>
          <div className="orline2"></div>
        </div>
        <button className="google-signup-button-signin" onClick={() => login()}>
          <img src={google} className="google-svg-signin" alt="Google Logo" />
          Sign up with Google
        </button>
        {errorMessage && <p className="error-message-signin">{errorMessage}</p>}
      </div>
    </div>
  );
}
